<template>
	<div>
		<drawer :name="DRAWERS.PRODUCT_VARIANTS">
			<product-variants-drawer :product="drawerProduct"></product-variants-drawer>
		</drawer>

		<drawer :name="DRAWERS.MANAGE_TAGS">
			<manage-tags-list-drawer :tags="moduleHeader.tags" :module="module"></manage-tags-list-drawer>
		</drawer>

		<div class="list-page">
			<div class="title-container">
				<div class="title">{{ products.length }} Product{{ products.length !== 1 ? 's' : '' }}</div>
				<module-page-buttons-component :show-tags="true">
					<router-link to="/commerce/products/categories" class="button"><div class="hover-title">Categories</div><span class="fa fa-list"></span></router-link>
				</module-page-buttons-component>
			</div>
			<div v-if="products.length > 5" class="search-container">
				<div class="search-box"><input type="text" placeholder="Search" v-model="search"></div>
				<div class="search-icon"><span class="fa fa-search"></span></div>
			</div>
			<div v-if="search" class="search-results">
				<div class="clear-button"><a href @click.prevent="search = ''">Clear Search<span class="fa fa-undo"></span></a></div>
				<div class="title-cell">
					<div class="title">Found <span>{{ filteredProducts.length }}</span> Result{{ filteredProducts.length !== 1 ? 's' : '' }}</div>
				</div>
			</div>
			<div class="table list">
				<nothing-here v-if="products.length === 0"></nothing-here>

				<div class="row sortable" v-if="products.length > 0">
					<div class="cell"><a href @click.prevent="sortByColumn('title')" :class="headerColumnClass('title')">Title<span v-if="sort.column === 'title'" class="fa" :class="sort.direction === 'asc' ? 'fa-angle-down' : 'fa-angle-up'"></span></a></div>
					<div class="cell"><a href @click.prevent="sortByColumn('variants')" :class="headerColumnClass('variants')">Variants<span v-if="sort.column === 'variants'" class="fa" :class="sort.direction === 'asc' ? 'fa-angle-down' : 'fa-angle-up'"></span></a></div>
					<div class="cell">Retail Range</div>
					<div class="cell">Categories</div>
					<div class="cell align-right buttons">
						<div v-if="canDragAndDrop">
							<a v-if="!isOrdering" href @click.prevent="enableDragging" class="button" data-ytta-id="-"><div class="hover-title">Custom Sort</div><span class="fa fa-sort" aria-hidden="true"></span></a>
							<a v-else href @click.prevent="isOrdering = false" class="button" data-ytta-id="-"><div class="hover-title">Stop Sorting</div><span class="fa fa-save" aria-hidden="true"></span></a>
						</div>
						<div v-else>
							<a href @click.prevent="resetSortingAndDragging" class="button"><div class="hover-title">{{ resetButtonText }}</div><span class="fa fa-sync" aria-hidden="true"></span></a>
						</div>
					</div>
				</div>
				<tbody v-sortable="{ onUpdate: reorderRows, handle: '.row', disabled: !isOrdering, scroll: true }">
					<product-list-item
						v-for="product in filteredProducts" :key="`product-${ product.id }`"
						:product="product"
						@variants="seeVariants" @delete="onDelete"
						class="non-heading"
					></product-list-item>
				</tbody>
			</div>
		</div>
	</div>
</template>

<script>

import _ from 'underscore';
import { mapGetters } from 'vuex';
import { DRAWERS } from '@/constants';
import ProductListItem from './components/ProductListItem';
import ProductVariantsDrawer from './drawers/ProductVariantsDrawer';
import ModulePageButtonsComponent from '../../modules/components/ModulePageButtonsComponent';
import ManageTagsListDrawer from '../../modules/drawers/ManageTagsListDrawer';

export default {

	name: 'ProductsListPage',

	async asyncData({ app, store, redirect }) {
		try {
			const [ listPayload, productPayload ] = await Promise.all([
				app.$api.get(`/module/products/list`),
				app.$api.get('/commerce/products'),
			]);

			store.dispatch('setActiveModule', listPayload.data.module);
			store.commit('setHeaderButtons', listPayload.data.boilerplate.buttons);
			store.commit('setPageData', listPayload.data.boilerplate.page);
			store.commit('setHeaderTags', { fields: listPayload.data.boilerplate.tagFields, numTags: listPayload.data.boilerplate.numTags });
			store.commit('setNavCount', { slug: listPayload.data.module.slug, count: listPayload.data.rows.length });

			return {
				products: productPayload.data.data,
				boilerplate: listPayload.data.boilerplate,
			};
		} catch (e) {
			if (e.response && e.response.status) {
				switch (e.response.status) {
					case 403:
						store.dispatch('errorToast', 'You do not have access to that section');
						break;
					default:
						store.dispatch('errorToast', 'There was an error loading that section');
						break;
				}
			}
			return redirect('/dashboard');
		}
	},

	data() {
		return {
			DRAWERS,
			search: '',
			boilerplate: {},
			products: [],
			drawerProduct: {},

			sort: {
				column: false,
				direction: null,
			},

			// Whether or not they are actively drag & drop sorting
			isOrdering: false,
		};
	},

	computed: {

		...mapGetters(['module', 'moduleHeader']),

		filteredProducts() {
			if (!this.search || this.search.length === 0) return this.sortedProducts;

			return this.sortedProducts.filter(product => {
				return product.title.toLowerCase().indexOf(this.search.toLowerCase()) !== -1;
			});
		},

		sortedProducts() {
			if (this.sort.column !== false && this.sort.direction !== false) {
				let sorted = _.sortBy(this.products, (row) => {
					// Sort based on field type
					switch (this.sort.column) {
						case 'variants':
							return Object.keys(row.variants).length;
						default:
							if (typeof row[ this.sort.column ] === 'string') {
								return row[ this.sort.column ].toLowerCase();
							}
							return row[ this.sort.column ];
					}
				});

				if (this.sort.direction === 'desc') {
					sorted = sorted.reverse();
				}

				return sorted;
			}

			return this.products;
		},

		resetButtonText() {
			let text = [];

			if (this.sort.column) {
				text.push('Reset Sorting');
			}

			if (this.searchTerm) {
				text.push('Clear Search');
			}

			return text.join(' & ');
		},

		canDragAndDrop() {
			return this.sort.column === false && !this.searchTerm;
		},

	},

	methods: {

		saveSort() {
			const order = this.sortedProducts.map((product, index) => {
				return { id: product.id, order: index };
			});

			this.$api.post(`/commerce/products/order`, { order }).then(() => {
				this.$store.dispatch('addToast', 'Sort Position Saved');
			});
		},

		enableDragging() {
			this.isOrdering = true;
			this.sort.column = false;
			this.$forceUpdate();
		},

		resetSortingAndDragging() {
			this.isOrdering = false;
			this.resetSort();
			this.searchTerm = '';
		},

		reorderRows(event) {
			this.sortedProducts.splice(event.newIndex, 0, this.sortedProducts.splice(event.oldIndex, 1)[ 0 ]);
			this.saveSort();
		},

		headerColumnClass(fieldName) {
			if (this.sort.column == fieldName) {
				if (this.sort.direction === 'asc') {
					return 'point-up';
				}
			} else {
				return 'point-down';
			}
		},

		sortByColumn(columnName) {
			const sortOptions = ['asc', 'desc', false];

			this.isOrdering = false; // Ensure they cannot drag & drop

			let direction = -1;
			if (this.sort.column === columnName) {
				direction = sortOptions.indexOf(this.sort.direction);
			}

			direction++;

			if (!sortOptions[ direction ]) {
				columnName = false;
			}

			this.sort = {
				column: columnName,
				direction: sortOptions[ direction ],
			};

		},

		seeVariants(product) {
			this.drawerProduct = product;
			this.$root.$emit('open-drawer', DRAWERS.PRODUCT_VARIANTS);
		},

		async onDelete({ product, done }) {
			try {
				await this.$api.delete(`/commerce/products/${product.id}`);

				this.$store.dispatch('loadModuleCounts');

				let index = this.products.indexOf(product);
				if (index !== -1) {
					this.products.splice(index, 1);
					this.$store.dispatch('addToast', 'Product Deleted');
				}
			} catch (e) {
				this.$store.dispatch('errorToast', 'There was a problem deleting that product');
			}

			done();

		}

	},

	components: {
		ProductListItem,
		ProductVariantsDrawer,
		ModulePageButtonsComponent,
		ManageTagsListDrawer,
	},
};

</script>
